import { useMutation, useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { formatJamaicaDateTime } from "@/lib/datetime";
import {
  AlertTriangle, CalendarDays, Loader2, RefreshCw, SlidersHorizontal, X,
} from "lucide-react";
import {
  RANGE_PRESETS, SEGMENT_LABELS, activeSegmentEntries, errorMessage, getSegmentOptions, getSyncStatus,
  metricsUrls, resolvePreset, runSync, segmentValueLabel, shortDate,
  type RangePreset, type Segment, type SegmentOptions, type SyncStatus,
} from "@/lib/metrics";

export interface HeaderState {
  preset: RangePreset;
  from: string;
  to: string;
  compare: boolean;
  segment: Segment;
}

const ANY = "__any__";

function invalidateMetrics() {
  queryClient.invalidateQueries({
    predicate: (q) => typeof q.queryKey[0] === "string" && (q.queryKey[0] as string).startsWith("/api/admin/metrics"),
  });
}

function SegmentSelect({
  label, value, onChange, options, placeholder,
}: {
  label: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  options: Array<{ value: string; label: string }>;
  placeholder: string;
}) {
  return (
    <div className="min-w-[9rem]">
      <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>
      <Select
        value={value || ANY}
        onValueChange={(v) => onChange(v === ANY ? undefined : v)}
      >
        <SelectTrigger className="h-9" data-testid={`select-segment-${label.toLowerCase()}`}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>{placeholder}</SelectItem>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function MetricsHeader({
  state, onChange,
}: {
  state: HeaderState;
  onChange: (next: HeaderState) => void;
}) {
  const { toast } = useToast();

  const { data: sync } = useQuery<SyncStatus>({
    queryKey: [metricsUrls.syncStatus()],
    queryFn: getSyncStatus,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const { data: options } = useQuery<SegmentOptions>({
    queryKey: [metricsUrls.segmentOptions()],
    queryFn: getSegmentOptions,
  });

  const syncMutation = useMutation({
    mutationFn: (full: boolean) => runSync(full),
    onSuccess: (res) => {
      toast({
        title: res.lastError ? "Sync finished with an error" : "Sync complete",
        description: res.lastError || `${res.counts.subscriptions.toLocaleString()} subscriptions, ${res.counts.invoices.toLocaleString()} invoices mirrored.`,
        variant: res.lastError ? "destructive" : undefined,
      });
      invalidateMetrics();
    },
    onError: (error) => toast({ title: "Sync failed", description: errorMessage(error), variant: "destructive" }),
  });

  const setPreset = (preset: RangePreset) => {
    if (preset === "custom") { onChange({ ...state, preset }); return; }
    const range = resolvePreset(preset);
    onChange({ ...state, preset, ...range });
  };

  const setSegmentKey = (key: keyof Segment, value: string | undefined) => {
    const segment = { ...state.segment };
    if (value === undefined) delete segment[key]; else segment[key] = value;
    onChange({ ...state, segment });
  };

  const chips = activeSegmentEntries(state.segment);
  const lastSync = sync?.lastIncrementalAt || sync?.lastFullAt || null;
  const needsFirstSync = !!sync && sync.counts.subscriptions === 0;
  const busy = syncMutation.isPending || !!sync?.running;

  return (
    <div className="space-y-3" data-testid="metrics-header">
      <Card>
        <CardContent className="pt-4 pb-4 space-y-3">
          {/* Range + compare + sync */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[11rem]">
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Period</Label>
              <Select value={state.preset} onValueChange={(v) => setPreset(v as RangePreset)}>
                <SelectTrigger className="h-9" data-testid="select-range-preset">
                  <CalendarDays className="w-3.5 h-3.5 mr-1 shrink-0 opacity-60" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RANGE_PRESETS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">From</Label>
              <Input
                type="date"
                value={state.from}
                className="h-9 w-[10.5rem]"
                onChange={(e) => onChange({ ...state, preset: "custom", from: e.target.value })}
                data-testid="input-range-from"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">To</Label>
              <Input
                type="date"
                value={state.to}
                className="h-9 w-[10.5rem]"
                onChange={(e) => onChange({ ...state, preset: "custom", to: e.target.value })}
                data-testid="input-range-to"
              />
            </div>

            <div className="flex items-center gap-2 h-9">
              <Switch
                id="metrics-compare"
                checked={state.compare}
                onCheckedChange={(v) => onChange({ ...state, compare: v })}
                data-testid="switch-compare"
              />
              <Label htmlFor="metrics-compare" className="cursor-pointer text-sm">Compare with previous period</Label>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <Badge variant="outline" className="font-normal gap-1.5 h-9 px-3" data-testid="pill-sync-status">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 opacity-60" />}
                {busy ? "Syncing Stripe…" : lastSync ? `Synced ${formatJamaicaDateTime(lastSync)}` : "Never synced"}
              </Badge>
              <Button
                size="sm"
                variant="outline"
                onClick={() => syncMutation.mutate(false)}
                disabled={busy}
                data-testid="button-sync-now"
              >
                Sync now
              </Button>
            </div>
          </div>

          {/* Segment bar */}
          <div className="flex flex-wrap items-end gap-3 border-t pt-3">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground h-9">
              <SlidersHorizontal className="w-3.5 h-3.5" /> Segment
            </div>
            <SegmentSelect
              label="Billing"
              placeholder="Any interval"
              value={state.segment.interval}
              onChange={(v) => setSegmentKey("interval", v)}
              options={(options?.intervals?.length ? options.intervals : ["month", "year"]).map((i) => ({
                value: i, label: i === "month" ? "Monthly" : "Yearly",
              }))}
            />
            <SegmentSelect
              label="Grade"
              placeholder="All grades"
              value={state.segment.grade}
              onChange={(v) => setSegmentKey("grade", v)}
              options={(options?.grades || []).map((g) => ({ value: String(g), label: `Grade ${g}` }))}
            />
            <SegmentSelect
              label="Plan"
              placeholder="All plans"
              value={state.segment.plan}
              onChange={(v) => setSegmentKey("plan", v)}
              options={(options?.plans || []).map((p) => ({ value: p.plan, label: `${p.plan} (${p.n})` }))}
            />
            <SegmentSelect
              label="Source"
              placeholder="All sources"
              value={state.segment.source}
              onChange={(v) => setSegmentKey("source", v)}
              options={(options?.sources || []).map((s) => ({ value: s.source, label: `${s.source} (${s.n})` }))}
            />
            <SegmentSelect
              label="Coupon"
              placeholder="Coupon or not"
              value={state.segment.coupon}
              onChange={(v) => setSegmentKey("coupon", v)}
              options={[{ value: "true", label: "On a coupon" }, { value: "false", label: "No coupon" }]}
            />

            {chips.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 h-9">
                {chips.map(([key, value]) => (
                  <Badge key={key} variant="secondary" className="font-normal gap-1" data-testid={`chip-segment-${key}`}>
                    {SEGMENT_LABELS[key]}: {segmentValueLabel(key, value)}
                    <button
                      type="button"
                      onClick={() => setSegmentKey(key, undefined)}
                      className="rounded-full hover:bg-background/60 p-0.5"
                      aria-label={`Remove ${SEGMENT_LABELS[key]} filter`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => onChange({ ...state, segment: {} })}>
                  Clear all
                </Button>
              </div>
            )}

            <div className="ml-auto text-xs text-muted-foreground h-9 flex items-center">
              {shortDate(state.from)} – {shortDate(state.to)}
            </div>
          </div>
        </CardContent>
      </Card>

      {(sync?.lastError || needsFirstSync) && (
        <Card className="border-amber-400/70">
          <CardContent className="py-3 flex flex-wrap items-center gap-3 text-sm">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="flex-1 min-w-[16rem]">
              {needsFirstSync
                ? "No Stripe data has been mirrored yet, so every number below will read zero. Run a full sync to pull subscriptions, invoices, charges and refunds."
                : `The last Stripe sync reported: ${sync?.lastError}`}
            </span>
            <Button
              size="sm"
              variant={needsFirstSync ? "default" : "outline"}
              onClick={() => syncMutation.mutate(needsFirstSync)}
              disabled={busy}
              data-testid="button-first-sync"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              {needsFirstSync ? "Run first sync" : "Retry sync"}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
